import { useEffect, useMemo, useState } from "react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import {
  BotonExportarExcel,
  Button,
  Card,
  Group,
  Input,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { CuentaContableConOrganizaciones } from "@/lib/types";
import {
  importarCuentasContables,
  listarCuentasContablesConOrganizaciones,
} from "../services/catalogos-contables-admin-service";
import { useCuentasAdmin } from "../hooks/use-catalogos-contables-admin";
import { useCrearCuenta, useEditarCuenta, useImportarCuentas } from "../hooks/use-catalogos-contables-mutations";
import { CecoConOrgsRow } from "./CecoConOrgsRow";
import { CecoCrearForm, type CopyElementoConOrgs } from "./CecoCrearForm";
import { ImportarExcelCatalogo } from "./ImportarExcelCatalogo";

const PAGE_SIZE = 15;

const EJEMPLOS_CUENTAS = [
  { codigo: "2219010100", nombre: "Proveedores nacionales (ejemplo · reemplázame)", numeroSociedad: "5200" },
  // Una misma cuenta puede asignarse a varias sociedades (relación M:N).
  { codigo: "601-01", nombre: "Servicios profesionales (ejemplo · reemplázame)", numeroSociedad: "5200" },
  { codigo: "601-01", nombre: "Servicios profesionales (ejemplo · reemplázame)", numeroSociedad: "5300" },
];

const COPY_CUENTA: CopyElementoConOrgs = {
  titulo: "Nueva Cuenta Contable",
  descripcion: "El código es inmutable tras la creación. Debes asignarla a una o más organizaciones.",
  placeholderCodigo: "601-01",
  placeholderNombre: "Servicios profesionales",
  maxCodigo: 40,
};

const NOTA_EDICION = "El código de la cuenta no se puede cambiar. Una cuenta puede asignarse a varias organizaciones.";

/**
 * Gestor de cuentas contables + sus organizaciones (D-8 · M:N, mismo modelo que los CeCos) ·
 * búsqueda + paginado server-side. Al crear una cuenta es obligatorio elegir al menos una
 * organización; el selector del prorrateo solo ofrece las cuentas de la organización de la solicitud.
 */
export function SeccionCuentas() {
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const serverParams = useMemo(
    () => ({
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [busquedaDebounced, page]
  );

  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useCuentasAdmin(serverParams);
  const cuentas = data?.items ?? [];
  const total = data?.total ?? 0;

  const [modoCrear, setModoCrear] = useState(false);
  const crear = useCrearCuenta({ onCreado: () => setModoCrear(false) });
  const editar = useEditarCuenta();
  const importar = useImportarCuentas();

  const obtenerTodas = async (): Promise<CuentaContableConOrganizaciones[]> => {
    const b = busquedaDebounced.trim();
    const todas: CuentaContableConOrganizaciones[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarCuentasContablesConOrganizaciones({ ...(b ? { busqueda: b } : {}), page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Input placeholder="Buscar por código, nombre u organización…" leftSection={<IconSearch size={14} stroke={1.7} />} value={busqueda} onChange={(e) => setBusqueda(e.currentTarget.value)} wrapperClassName="w-80" />
        <Group gap="xs">
          <ImportarExcelCatalogo importar={importar} previsualizar={(filas) => importarCuentasContables(filas, true)} plantillaArchivo="plantilla-cuentas-contables" plantillaHoja="Cuentas contables" ejemplos={EJEMPLOS_CUENTAS} incluyeSociedad />
          {!modoCrear ? (
            <Button leftSection={<IconPlus size={14} />} onClick={() => setModoCrear(true)}>
              Nueva cuenta
            </Button>
          ) : null}
        </Group>
      </Group>
      {modoCrear ? (
        <CecoCrearForm copy={COPY_CUENTA} loading={crear.isPending} onCancelar={() => setModoCrear(false)} onCrear={crear.mutate} />
      ) : null}
      <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
      <Card padding="lg">
        <Group justify="flex-end" className="mb-2">
          <BotonExportarExcel
            archivo="cuentas-contables"
            hoja="Cuentas contables"
            obtenerFilas={obtenerTodas}
            columnas={[
              { header: "Código", value: (c) => c.codigo },
              { header: "Nombre", value: (c) => c.nombre },
              { header: "Organizaciones", value: (c) => c.organizaciones.map((o) => `${o.numeroSociedad} · ${o.nombre}`).join(" | ") },
            ]}
          />
        </Group>
        <div className="overflow-x-auto">
          <Table verticalSpacing="sm">
            <Thead>
              <Tr>
                <Th>Código</Th>
                <Th>Nombre</Th>
                <Th>Organizaciones</Th>
                <Th />
              </Tr>
            </Thead>
            {!data ? (
              <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, { align: "right" }]} filas={PAGE_SIZE} />
            ) : (
              <Tbody>
                {cuentas.map((cuenta) => (
                  <CecoConOrgsRow
                    key={cuenta.id}
                    ceco={cuenta}
                    etiqueta="cuenta"
                    nota={NOTA_EDICION}
                    onGuardar={editar.mutate}
                    isPending={editar.isPending}
                  />
                ))}
              </Tbody>
            )}
          </Table>
        </div>
      </Card>
      <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="cuentas" />
    </Stack>
  );
}
